// =============================================================================
// SEVRIC Battery Care — shared Firestore subscriptions for the Today dashboard.
//
// One listener per collection across the whole page, no widget-level
// re-subscription. Every widget gets the same `data`, the same loading flag,
// and a consistent shape — so swapping or adding a widget never costs an
// extra read.
//
// All hooks return `{ data, loading, error }` and `data` is always an array.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { useActiveStore } from "./activeStore.js";

// -----------------------------------------------------------------------------
// useBatteryUnits — every unit for the caller's tenant + store. Live.
// -----------------------------------------------------------------------------
export function useBatteryUnits() {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId || null;
  // Scope to the *active* store (the topbar switcher), not the default
  // profile.storeId — otherwise the dashboard keeps showing the default
  // store's data after a multi-store admin switches stores.
  const storeId  = useActiveStore() || profile?.storeId || null;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    // Filter by tenantId (always) and storeId when we have one. If the user
    // has multi-store access we still scope to their *current* store so the
    // dashboard reads cleanly — switching stores re-subscribes.
    const filters = [where("tenantId", "==", tenantId)];
    if (storeId) filters.push(where("storeId", "==", storeId));
    const q = query(collection(db, "batteryUnits"), ...filters);
    const unsub = onSnapshot(q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setData(rows); setLoading(false); setError(null);
      },
      (err) => {
        console.warn("[useBatteryUnits] subscribe failed:", err?.message);
        setError(err); setLoading(false);
      }
    );
    return unsub;
  }, [tenantId, storeId]);

  return { data, loading, error, tenantId, storeId };
}

// -----------------------------------------------------------------------------
// Derived signals — small pure helpers so widgets don't repeat the math.
// -----------------------------------------------------------------------------

// Voltage bucket for a single 12V battery reading.
//   ok    >= 12.6V
//   fair  12.4-12.59
//   warn  12.0-12.39
//   risk  < 12.0
// HV (traction) batteries we leave neutral — they're informational only.
export function voltageBucket(unit) {
  if (!unit) return "unknown";
  if (unit.voltageType === "HV") return "hv";
  const v = Number(unit.voltage);
  if (!Number.isFinite(v)) return "unknown";
  if (v >= 12.6) return "ok";
  if (v >= 12.4) return "fair";
  if (v >= 12.0) return "warn";
  return "risk";
}

// True if this unit's voltage dropped >= 0.3V between its two most recent
// recorded readings (i.e., it's degrading even if the absolute number is
// still in the "fair" or "warn" band).
export function isDropping(unit) {
  const h = Array.isArray(unit?.history) ? unit.history : [];
  if (h.length < 2) return false;
  // History is appended chronologically; take the last two readings that
  // actually carry a voltage.
  const withV = h.filter((e) => Number.isFinite(Number(e?.voltage)));
  if (withV.length < 2) return false;
  const latest = Number(withV[withV.length - 1].voltage);
  const prior  = Number(withV[withV.length - 2].voltage);
  return prior - latest >= 0.3;
}

// Days since this unit's last check. Returns null if unknown.
export function daysSinceCheck(unit) {
  const h = Array.isArray(unit?.history) ? unit.history : [];
  if (h.length === 0) return null;
  const lastAt = h[h.length - 1]?.at;
  const ms = lastAt?.toMillis?.() || (typeof lastAt === "number" ? lastAt : 0) || (lastAt?.seconds ? lastAt.seconds * 1000 : 0);
  if (!ms) return null;
  return Math.floor((Date.now() - ms) / (1000 * 60 * 60 * 24));
}

export function relativeWhen(ts) {
  const ms = ts?.toMillis?.() || (typeof ts === "number" ? ts : 0) || (ts?.seconds ? ts.seconds * 1000 : 0);
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Group units into risk buckets — useful for several widgets.
export function partitionByRisk(units) {
  const risk = [];   // < 12.0V
  const warn = [];   // 12.0–12.39V or dropping
  const stale = [];  // no check in 7+ days
  const ok = [];
  for (const u of units) {
    const b = voltageBucket(u);
    if (b === "risk") { risk.push(u); continue; }
    if (b === "warn" || isDropping(u)) { warn.push(u); continue; }
    const d = daysSinceCheck(u);
    if (d !== null && d >= 7) { stale.push(u); continue; }
    ok.push(u);
  }
  return { risk, warn, stale, ok };
}

// Recent activity across all units, flattened + sorted newest-first.
export function recentActivity(units, limit = 12) {
  const events = [];
  for (const u of units) {
    const h = Array.isArray(u.history) ? u.history : [];
    for (let i = 0; i < h.length; i++) {
      const e = h[i];
      const ms = e.at?.toMillis?.() || (typeof e.at === "number" ? e.at : 0) || (e.at?.seconds ? e.at.seconds * 1000 : 0);
      events.push({
        unitId: u.id,
        stockNumber: u.stockNumber || "",
        year: u.year, make: u.make, model: u.model,
        at: e.at, atMs: ms,
        by: e.by || "Someone",
        action: e.action || "checked",
        voltage: e.voltage,
        notes: e.notes,
      });
    }
  }
  events.sort((a, b) => (b.atMs || 0) - (a.atMs || 0));
  return events.slice(0, limit);
}

// Pick the most recently touched unit for the Historian widget.
export function mostRecentlyTouched(units) {
  let best = null;
  let bestMs = 0;
  for (const u of units) {
    const h = Array.isArray(u.history) ? u.history : [];
    const last = h[h.length - 1];
    const ms = last?.at?.toMillis?.() || 0;
    if (ms > bestMs) { best = u; bestMs = ms; }
  }
  return best;
}

// Friendly vehicle name.
export function unitName(u) {
  return [u.year, u.make, u.model].filter(Boolean).join(" ") || "Unnamed unit";
}

// ----------------------------------------------------------------------------
// Author rendering — single source of truth for every widget that shows
// "who did what". Author objects on history entries are stored as
// `{uid, email, displayName, initials}` by authorFromProfile(). Always
// prefer the snapshotted displayName so old entries keep showing the user's
// real name even if their email changes later.
// ----------------------------------------------------------------------------

// Render the full friendly name. "Andrew Monahan" if displayName is set,
// otherwise fall back to the email's local-part title-cased, otherwise
// "Someone". Never returns "[object Object]".
//
// `opts.self` is the CURRENT user. If the entry was authored by the same
// uid, we use the current displayName instead of whatever was snapshotted
// at write time — this fixes historical entries that were written before
// the displayName field existed.
export function authorName(who, opts) {
  if (opts?.self?.uid && who?.uid === opts.self.uid && opts.self.displayName) {
    return opts.self.displayName;
  }
  if (!who) return "Someone";
  if (typeof who === "string") return titleCaseFirstToken(who) || "Someone";
  return who.displayName || who.name || titleCaseFirstToken(who.email) || who.initials || "Someone";
}

// Just the first name — used in Ops Log rows so the line doesn't wrap.
// "Andrew Monahan" → "Andrew".
export function authorFirstName(who, opts) {
  const full = authorName(who, opts);
  const first = String(full).trim().split(/\s+/).filter(Boolean)[0];
  return first || "Someone";
}

// Two-letter initials. Prefers the resolved name when we have a self
// override (so old "AM" snapshots don't override a freshly-set
// "Andrew Monahan"), otherwise the snapshot's `initials` field, otherwise
// derive from name.
export function authorInitials(who, opts) {
  const usedSelfOverride =
    opts?.self?.uid && who?.uid === opts.self.uid && opts.self.displayName;
  if (!usedSelfOverride && who && typeof who === "object" && who.initials) {
    return String(who.initials).slice(0, 2).toUpperCase();
  }
  const full = authorName(who, opts);
  const parts = String(full).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || "?").slice(0, 2).toUpperCase();
}

function titleCaseFirstToken(s) {
  const str = String(s || "").trim();
  if (!str) return "";
  const first = str.split(/[ @._]/).filter(Boolean)[0] || str;
  return first[0].toUpperCase() + first.slice(1);
}
